import { FormCard } from "@/components/FormCard"
import { DatePicker } from "@/components/DatePicker"
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldRow,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import {
  identityDocumentTypes,
  type OwnerTabState,
  type OwnerType,
  type RecipientMode,
} from "@/lib/documentEditor"

function IdentityDocumentSelect({
  id,
  value,
  onChange,
}: {
  id: string
  value: string
  onChange: (value: string) => void
}) {
  return (
    <Select onValueChange={onChange} value={value}>
      <SelectTrigger id={id} length="full-size">
        <SelectValue placeholder="Оберіть тип документа" />
      </SelectTrigger>
      <SelectContent>
        {identityDocumentTypes.map((type) => (
          <SelectItem key={type} value={type}>
            {type}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export function OwnerTab({
  value,
  errors,
  onChange,
  onCommit,
}: {
  value: OwnerTabState
  /** Per-field messages from shared's `editableDraftFields` — see DocumentEdit.tsx's `fieldErrors`. Only set for fields a free-text input could actually get wrong (Select/RadioGroup-backed fields can't). */
  errors: Partial<Record<keyof OwnerTabState, string>>
  onChange: (patch: Partial<OwnerTabState>) => void
  /** Autosaves a patch immediately (select/radio fields) — text fields autosave on blur instead. */
  onCommit: (patch: Partial<OwnerTabState>) => void
}) {
  const isIndividual = value.ownerType === "INDIVIDUAL"
  const showRecipientBlock =
    value.ownerType === "LEGAL_ENTITY" || value.recipientMode === "poa"

  return (
    <div className="flex flex-col gap-4">
      <FormCard title="Власник">
        <FieldGroup className="gap-4">
          <Field>
            <FieldLabel>Тип власника</FieldLabel>
            <RadioGroup
              className="flex gap-5 pt-1"
              onValueChange={(next) => {
                const ownerType = next as OwnerType
                // Mirrors the backend's own `normalizeDraft` (documents-service.ts),
                // which unsets the other type's fields server-side as soon as
                // `owner.type` changes — clear them here too so the UI never
                // keeps showing values that no longer exist on the document.
                const patch: Partial<OwnerTabState> =
                  ownerType === "INDIVIDUAL"
                    ? { ownerType, orgName: "", edrpou: "" }
                    : { ownerType, fullName: "", taxId: "" }
                onChange(patch)
                onCommit(patch)
              }}
              value={value.ownerType}
            >
              <label
                className="flex items-center gap-2 text-sm"
                htmlFor="owner-type-individual"
              >
                <RadioGroupItem id="owner-type-individual" value="INDIVIDUAL" />
                Фізична особа
              </label>
              <label
                className="flex items-center gap-2 text-sm"
                htmlFor="owner-type-legal"
              >
                <RadioGroupItem id="owner-type-legal" value="LEGAL_ENTITY" />
                Юридична особа
              </label>
            </RadioGroup>
          </Field>

          {isIndividual ? (
            <>
              <FieldRow>
                <Field data-invalid={Boolean(errors.fullName)} required>
                  <FieldLabel htmlFor="owner-full-name">ПІБ</FieldLabel>
                  <Input
                    aria-invalid={Boolean(errors.fullName)}
                    id="owner-full-name"
                    onBlur={() => onCommit({ fullName: value.fullName })}
                    onChange={(e) => onChange({ fullName: e.target.value })}
                    placeholder="Прізвище Ім'я По батькові"
                    value={value.fullName}
                  />
                  {errors.fullName && <FieldError>{errors.fullName}</FieldError>}
                </Field>
              </FieldRow>
              <FieldRow>
                <Field data-invalid={Boolean(errors.birthDate)}>
                  <FieldLabel htmlFor="owner-birth-date">
                    Дата народження
                  </FieldLabel>
                  <DatePicker
                    id="owner-birth-date"
                    onChange={(next) => {
                      onChange({ birthDate: next })
                      onCommit({ birthDate: next })
                    }}
                    value={value.birthDate}
                  />
                  {errors.birthDate && <FieldError>{errors.birthDate}</FieldError>}
                </Field>
                <Field data-invalid={Boolean(errors.taxId)} required>
                  <FieldLabel htmlFor="owner-tax-id">ІПН / РНОКПП</FieldLabel>
                  <Input
                    aria-invalid={Boolean(errors.taxId)}
                    id="owner-tax-id"
                    maxLength={10}
                    onBlur={() => onCommit({ taxId: value.taxId })}
                    onChange={(e) => onChange({ taxId: e.target.value })}
                    placeholder="1234567890"
                    value={value.taxId}
                  />
                  {errors.taxId && <FieldError>{errors.taxId}</FieldError>}
                </Field>
              </FieldRow>
            </>
          ) : (
            <FieldRow>
              <Field data-invalid={Boolean(errors.orgName)} required>
                <FieldLabel htmlFor="owner-org-name">
                  Назва організації
                </FieldLabel>
                <Input
                  aria-invalid={Boolean(errors.orgName)}
                  id="owner-org-name"
                  onBlur={() => onCommit({ orgName: value.orgName })}
                  onChange={(e) => onChange({ orgName: e.target.value })}
                  placeholder="Повна назва юридичної особи"
                  value={value.orgName}
                />
                {errors.orgName && <FieldError>{errors.orgName}</FieldError>}
              </Field>
              <Field data-invalid={Boolean(errors.edrpou)} required>
                <FieldLabel htmlFor="owner-edrpou">ЄДРПОУ</FieldLabel>
                <Input
                  aria-invalid={Boolean(errors.edrpou)}
                  id="owner-edrpou"
                  onBlur={() => onCommit({ edrpou: value.edrpou })}
                  onChange={(e) => onChange({ edrpou: e.target.value })}
                  placeholder="12345678"
                  value={value.edrpou}
                />
                {errors.edrpou && <FieldError>{errors.edrpou}</FieldError>}
              </Field>
            </FieldRow>
          )}

          <FieldRow>
            <Field data-invalid={Boolean(errors.phone)}>
              <FieldLabel htmlFor="owner-phone">Телефон</FieldLabel>
              <Input
                aria-invalid={Boolean(errors.phone)}
                id="owner-phone"
                onBlur={() => onCommit({ phone: value.phone })}
                onChange={(e) => onChange({ phone: e.target.value })}
                placeholder="+380XXXXXXXXX"
                value={value.phone}
              />
              {errors.phone && <FieldError>{errors.phone}</FieldError>}
            </Field>
            <Field data-invalid={Boolean(errors.email)}>
              <FieldLabel htmlFor="owner-email">Ел. пошта</FieldLabel>
              <Input
                aria-invalid={Boolean(errors.email)}
                id="owner-email"
                onBlur={() => onCommit({ email: value.email })}
                onChange={(e) => onChange({ email: e.target.value })}
                placeholder="email@example.com"
                type="email"
                value={value.email}
              />
              {errors.email && <FieldError>{errors.email}</FieldError>}
            </Field>
          </FieldRow>

          <FieldRow>
            <Field data-invalid={Boolean(errors.address)}>
              <FieldLabel htmlFor="owner-address">Поштова адреса</FieldLabel>
              <Input
                aria-invalid={Boolean(errors.address)}
                id="owner-address"
                onBlur={() => onCommit({ address: value.address })}
                onChange={(e) => onChange({ address: e.target.value })}
                placeholder="Область, місто, вулиця, будинок"
                value={value.address}
              />
              {errors.address && <FieldError>{errors.address}</FieldError>}
            </Field>
          </FieldRow>

          {isIndividual && (
            <>
              <FieldRow>
                <Field length="6x" required>
                  <FieldLabel htmlFor="owner-id-doc-type">
                    Тип документа, що посвідчує особу
                  </FieldLabel>
                  <IdentityDocumentSelect
                    id="owner-id-doc-type"
                    onChange={(next) => {
                      onChange({ idDocType: next })
                      onCommit({ idDocType: next })
                    }}
                    value={value.idDocType}
                  />
                </Field>
                <Field data-invalid={Boolean(errors.idDocSeries)} length="3x">
                  <FieldLabel htmlFor="owner-id-doc-series">
                    Серія документа
                  </FieldLabel>
                  <Input
                    aria-invalid={Boolean(errors.idDocSeries)}
                    id="owner-id-doc-series"
                    onBlur={() =>
                      onCommit({ idDocSeries: value.idDocSeries })
                    }
                    onChange={(e) =>
                      onChange({ idDocSeries: e.target.value })
                    }
                    placeholder="СН"
                    value={value.idDocSeries}
                  />
                  {errors.idDocSeries && (
                    <FieldError>{errors.idDocSeries}</FieldError>
                  )}
                </Field>
                <Field data-invalid={Boolean(errors.idDocNumber)} required>
                  <FieldLabel htmlFor="owner-id-doc-number">
                    Номер документа
                  </FieldLabel>
                  <Input
                    aria-invalid={Boolean(errors.idDocNumber)}
                    id="owner-id-doc-number"
                    onBlur={() =>
                      onCommit({ idDocNumber: value.idDocNumber })
                    }
                    onChange={(e) =>
                      onChange({ idDocNumber: e.target.value })
                    }
                    placeholder="123456"
                    value={value.idDocNumber}
                  />
                  {errors.idDocNumber && (
                    <FieldError>{errors.idDocNumber}</FieldError>
                  )}
                </Field>
              </FieldRow>
              <FieldRow>
                <Field data-invalid={Boolean(errors.idDocIssuedBy)}>
                  <FieldLabel htmlFor="owner-id-doc-issued-by">
                    Ким виданий
                  </FieldLabel>
                  <Input
                    aria-invalid={Boolean(errors.idDocIssuedBy)}
                    id="owner-id-doc-issued-by"
                    onBlur={() =>
                      onCommit({ idDocIssuedBy: value.idDocIssuedBy })
                    }
                    onChange={(e) =>
                      onChange({ idDocIssuedBy: e.target.value })
                    }
                    placeholder="Орган, що видав документ"
                    value={value.idDocIssuedBy}
                  />
                  {errors.idDocIssuedBy && (
                    <FieldError>{errors.idDocIssuedBy}</FieldError>
                  )}
                </Field>
                <Field data-invalid={Boolean(errors.idDocIssuedAt)} length="4x">
                  <FieldLabel htmlFor="owner-id-doc-issued-at">
                    Дата видачі
                  </FieldLabel>
                  <DatePicker
                    id="owner-id-doc-issued-at"
                    onChange={(next) => {
                      onChange({ idDocIssuedAt: next })
                      onCommit({ idDocIssuedAt: next })
                    }}
                    value={value.idDocIssuedAt}
                  />
                  {errors.idDocIssuedAt && (
                    <FieldError>{errors.idDocIssuedAt}</FieldError>
                  )}
                </Field>
              </FieldRow>
            </>
          )}

          <Field className="mt-6 border-t pt-4">
            <FieldLabel htmlFor="owner-document-notes">
              Примітки до документа
            </FieldLabel>
            <Textarea
              id="owner-document-notes"
              onChange={(e) => onChange({ documentNotes: e.target.value })}
              placeholder="Додаткові примітки до документа"
              rows={3}
              value={value.documentNotes}
            />
          </Field>
        </FieldGroup>
      </FormCard>

      <FormCard title="Отримувач">
        <div className="flex flex-col gap-4">
          {isIndividual ? (
            <div className="flex flex-wrap gap-4">
              <RadioGroup
                className="flex flex-wrap gap-4"
                onValueChange={(next) => {
                  onChange({ recipientMode: next as RecipientMode })
                  onCommit({ recipientMode: next as RecipientMode })
                }}
                value={value.recipientMode}
              >
                <label
                  className="flex items-center gap-2 text-sm"
                  htmlFor="recipient-mode-owner"
                >
                  <RadioGroupItem id="recipient-mode-owner" value="owner" />
                  Отримувач є власником ТЗ
                </label>
                <label
                  className="flex items-center gap-2 text-sm"
                  htmlFor="recipient-mode-poa"
                >
                  <RadioGroupItem id="recipient-mode-poa" value="poa" />
                  За довіреністю
                </label>
              </RadioGroup>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Для юридичної особи дані отримувача зазначаються окремо.
            </p>
          )}

          {showRecipientBlock && (
            <div className="mt-4 flex flex-col gap-4">
              {isIndividual && (
                <FieldRow>
                  <Field data-invalid={Boolean(errors.receiptDocSeries)}>
                    <FieldLabel htmlFor="recipient-receipt-doc-series">
                      Серія документа
                    </FieldLabel>
                    <Input
                      aria-invalid={Boolean(errors.receiptDocSeries)}
                      id="recipient-receipt-doc-series"
                      onBlur={() =>
                        onCommit({ receiptDocSeries: value.receiptDocSeries })
                      }
                      onChange={(e) =>
                        onChange({ receiptDocSeries: e.target.value })
                      }
                      placeholder="СН"
                      value={value.receiptDocSeries}
                    />
                    {errors.receiptDocSeries && (
                      <FieldError>{errors.receiptDocSeries}</FieldError>
                    )}
                  </Field>
                  <Field data-invalid={Boolean(errors.receiptDocNumber)}>
                    <FieldLabel htmlFor="recipient-receipt-doc-number">
                      Номер документа
                    </FieldLabel>
                    <Input
                      aria-invalid={Boolean(errors.receiptDocNumber)}
                      id="recipient-receipt-doc-number"
                      onBlur={() =>
                        onCommit({ receiptDocNumber: value.receiptDocNumber })
                      }
                      onChange={(e) =>
                        onChange({ receiptDocNumber: e.target.value })
                      }
                      placeholder="123456"
                      value={value.receiptDocNumber}
                    />
                    {errors.receiptDocNumber && (
                      <FieldError>{errors.receiptDocNumber}</FieldError>
                    )}
                  </Field>
                  <Field data-invalid={Boolean(errors.receiptDocDate)}>
                    <FieldLabel htmlFor="recipient-receipt-doc-date">
                      Дата документа отримання
                    </FieldLabel>
                    <DatePicker
                      id="recipient-receipt-doc-date"
                      onChange={(next) => {
                        onChange({ receiptDocDate: next })
                        onCommit({ receiptDocDate: next })
                      }}
                      value={value.receiptDocDate}
                    />
                    {errors.receiptDocDate && (
                      <FieldError>{errors.receiptDocDate}</FieldError>
                    )}
                  </Field>
                </FieldRow>
              )}
              <FieldGroup className="gap-4">
                <FieldRow>
                  <Field data-invalid={Boolean(errors.recFullName)} required>
                    <FieldLabel htmlFor="recipient-full-name">ПІБ</FieldLabel>
                    <Input
                      aria-invalid={Boolean(errors.recFullName)}
                      id="recipient-full-name"
                      onBlur={() =>
                        onCommit({ recFullName: value.recFullName })
                      }
                      onChange={(e) =>
                        onChange({ recFullName: e.target.value })
                      }
                      placeholder="Прізвище Ім'я По батькові"
                      value={value.recFullName}
                    />
                    {errors.recFullName && (
                      <FieldError>{errors.recFullName}</FieldError>
                    )}
                  </Field>
                  <Field data-invalid={Boolean(errors.recBirthDate)}>
                    <FieldLabel htmlFor="recipient-birth-date">
                      Дата народження
                    </FieldLabel>
                    <DatePicker
                      id="recipient-birth-date"
                      onChange={(next) => {
                        onChange({ recBirthDate: next })
                        onCommit({ recBirthDate: next })
                      }}
                      value={value.recBirthDate}
                    />
                    {errors.recBirthDate && (
                      <FieldError>{errors.recBirthDate}</FieldError>
                    )}
                  </Field>
                </FieldRow>
                <FieldRow>
                  <Field length="6x">
                    <FieldLabel htmlFor="recipient-id-doc-type">
                      Тип документа, що посвідчує особу
                    </FieldLabel>
                    <IdentityDocumentSelect
                      id="recipient-id-doc-type"
                      onChange={(next) => {
                        onChange({ recIdDocType: next })
                        onCommit({ recIdDocType: next })
                      }}
                      value={value.recIdDocType}
                    />
                  </Field>
                  <Field data-invalid={Boolean(errors.recIdDocSeries)} length="3x">
                    <FieldLabel htmlFor="recipient-id-doc-series">
                      Серія документа
                    </FieldLabel>
                    <Input
                      aria-invalid={Boolean(errors.recIdDocSeries)}
                      id="recipient-id-doc-series"
                      onBlur={() =>
                        onCommit({ recIdDocSeries: value.recIdDocSeries })
                      }
                      onChange={(e) =>
                        onChange({ recIdDocSeries: e.target.value })
                      }
                      placeholder="СН"
                      value={value.recIdDocSeries}
                    />
                    {errors.recIdDocSeries && (
                      <FieldError>{errors.recIdDocSeries}</FieldError>
                    )}
                  </Field>
                  <Field data-invalid={Boolean(errors.recIdDocNumber)}>
                    <FieldLabel htmlFor="recipient-id-doc-number">
                      Номер документа
                    </FieldLabel>
                    <Input
                      aria-invalid={Boolean(errors.recIdDocNumber)}
                      id="recipient-id-doc-number"
                      onBlur={() =>
                        onCommit({ recIdDocNumber: value.recIdDocNumber })
                      }
                      onChange={(e) =>
                        onChange({ recIdDocNumber: e.target.value })
                      }
                      placeholder="123456"
                      value={value.recIdDocNumber}
                    />
                    {errors.recIdDocNumber && (
                      <FieldError>{errors.recIdDocNumber}</FieldError>
                    )}
                  </Field>
                </FieldRow>
                <FieldRow>
                  <Field data-invalid={Boolean(errors.recIdDocIssuedBy)}>
                    <FieldLabel htmlFor="recipient-id-doc-issued-by">
                      Ким виданий
                    </FieldLabel>
                    <Input
                      aria-invalid={Boolean(errors.recIdDocIssuedBy)}
                      id="recipient-id-doc-issued-by"
                      onBlur={() =>
                        onCommit({ recIdDocIssuedBy: value.recIdDocIssuedBy })
                      }
                      onChange={(e) =>
                        onChange({ recIdDocIssuedBy: e.target.value })
                      }
                      placeholder="Орган, що видав документ"
                      value={value.recIdDocIssuedBy}
                    />
                    {errors.recIdDocIssuedBy && (
                      <FieldError>{errors.recIdDocIssuedBy}</FieldError>
                    )}
                  </Field>
                  <Field data-invalid={Boolean(errors.recIdDocIssuedAt)} length="4x">
                    <FieldLabel htmlFor="recipient-id-doc-issued-at">
                      Дата видачі
                    </FieldLabel>
                    <DatePicker
                      id="recipient-id-doc-issued-at"
                      onChange={(next) => {
                        onChange({ recIdDocIssuedAt: next })
                        onCommit({ recIdDocIssuedAt: next })
                      }}
                      value={value.recIdDocIssuedAt}
                    />
                    {errors.recIdDocIssuedAt && (
                      <FieldError>{errors.recIdDocIssuedAt}</FieldError>
                    )}
                  </Field>
                </FieldRow>
              </FieldGroup>
            </div>
          )}
        </div>
      </FormCard>
    </div>
  )
}
